import React from "react";
import {
  History,
  Landmark,
  CreditCard,
  Banknote,
  FileCheck,
  ShieldAlert,
} from "lucide-react";
import {
  StaffDetails,
  SalaryPaymentMode,
  SalaryPaymentModeLabels,
} from "../../../types/staff.types";

interface SalaryTabProps {
  details: StaffDetails | null;
  isEditing: boolean;
  editFormData: any;
  readOnly: boolean;
  onOpenSalaryRevisions: () => void;
  onChange: (field: string, value: any) => void;
}

export const SalaryTab: React.FC<SalaryTabProps> = ({
  details,
  isEditing,
  editFormData,
  readOnly,
  onOpenSalaryRevisions,
  onChange,
}) => {
  if (isEditing) {
    return (
      <div className="space-y-6">
        {/* Section 1: Salary & Mode */}
        <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-900 border-b border-gray-200/80 pb-2.5">
            <Banknote className="w-4 h-4 text-emerald-600" />
            <span>Monthly Compensation & Mode</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Monthly Salary (₹)
              </label>
              <input
                type="number"
                value={editFormData.monthlySalary || ""}
                onChange={(e) => onChange("monthlySalary", e.target.value)}
                placeholder="e.g. 45000"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 font-bold text-gray-900"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Payment Mode
              </label>
              <select
                value={editFormData.salaryPaymentMode || ""}
                onChange={(e) => onChange("salaryPaymentMode", e.target.value)}
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              >
                {Object.values(SalaryPaymentMode).map((m) => (
                  <option key={m} value={m}>
                    {SalaryPaymentModeLabels[m]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                UPI ID (Optional)
              </label>
              <input
                type="text"
                value={editFormData.upiId || ""}
                onChange={(e) => onChange("upiId", e.target.value)}
                placeholder="username@okhdfcbank"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              />
            </div>
          </div>
        </div>

        {/* Section 2: Bank Account Details */}
        <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-900 border-b border-gray-200/80 pb-2.5">
            <Landmark className="w-4 h-4 text-emerald-600" />
            <span>Bank Account & Statutory Identifiers</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Bank Name
              </label>
              <input
                type="text"
                value={editFormData.bankName || ""}
                onChange={(e) => onChange("bankName", e.target.value)}
                placeholder="e.g. HDFC Bank, ICICI Bank"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Account Number
              </label>
              <input
                type="text"
                value={editFormData.accountNumber || ""}
                onChange={(e) => onChange("accountNumber", e.target.value)}
                placeholder="Account number"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                IFSC Code
              </label>
              <input
                type="text"
                value={editFormData.ifscCode || ""}
                onChange={(e) => onChange("ifscCode", e.target.value)}
                placeholder="e.g. HDFC0001234"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 uppercase font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                PAN Number
              </label>
              <input
                type="text"
                value={editFormData.panNumber || ""}
                onChange={(e) => onChange("panNumber", e.target.value)}
                placeholder="ABCDE1234F"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 uppercase font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                UAN / PF Number
              </label>
              <input
                type="text"
                value={editFormData.uanPfNumber || ""}
                onChange={(e) => onChange("uanPfNumber", e.target.value)}
                placeholder="12-digit UAN"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                ESIC Number
              </label>
              <input
                type="text"
                value={editFormData.esicNumber || ""}
                onChange={(e) => onChange("esicNumber", e.target.value)}
                placeholder="ESIC Number"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 font-mono"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card 1: Salary & Mode */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-2xs space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
              <Banknote className="w-4 h-4 text-emerald-600" />
              <span>Salary & Payment Method</span>
            </div>
            {!readOnly && (
              <button
                type="button"
                onClick={onOpenSalaryRevisions}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200 transition-colors"
              >
                <History className="w-3.5 h-3.5" />
                <span>Revision History</span>
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-y-3.5 text-xs">
            <div>
              <p className="text-gray-400 font-medium">Monthly Compensation</p>
              <p className="text-emerald-700 font-bold text-base mt-0.5">
                ₹{details?.monthlySalary ? details.monthlySalary.toLocaleString("en-IN") : "0"}
              </p>
            </div>
            <div>
              <p className="text-gray-400 font-medium">Payment Mode</p>
              <p className="text-gray-800 font-medium mt-0.5">
                {SalaryPaymentModeLabels[details?.salaryPaymentMode as SalaryPaymentMode] || details?.salaryPaymentMode || "Bank Transfer"}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-gray-400 font-medium">UPI ID</p>
              <p className="text-gray-800 font-mono font-medium mt-0.5">{details?.upiId || "N/A"}</p>
            </div>
          </div>
        </div>

        {/* Card 2: Bank & Statutory */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-2xs space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100 text-sm font-bold text-gray-900">
            <Landmark className="w-4 h-4 text-emerald-600" />
            <span>Bank Account & Statutory Details</span>
          </div>

          <div className="grid grid-cols-2 gap-y-3.5 text-xs">
            <div>
              <p className="text-gray-400 font-medium">Bank Name</p>
              <p className="text-gray-900 font-semibold text-sm mt-0.5">{details?.bankName || "N/A"}</p>
            </div>
            <div>
              <p className="text-gray-400 font-medium">Account Number</p>
              <p className="text-gray-800 font-mono font-medium mt-0.5">{details?.accountNumber || "N/A"}</p>
            </div>
            <div>
              <p className="text-gray-400 font-medium">IFSC Code</p>
              <p className="text-gray-800 font-mono font-medium mt-0.5">{details?.ifscCode || "N/A"}</p>
            </div>
            <div>
              <p className="text-gray-400 font-medium">PAN Number</p>
              <p className="text-gray-800 font-mono font-medium mt-0.5">{details?.panNumber || "N/A"}</p>
            </div>
            <div>
              <p className="text-gray-400 font-medium">UAN / PF</p>
              <p className="text-gray-800 font-mono font-medium mt-0.5">{details?.uanPfNumber || "N/A"}</p>
            </div>
            <div>
              <p className="text-gray-400 font-medium">ESIC Number</p>
              <p className="text-gray-800 font-mono font-medium mt-0.5">{details?.esicNumber || "N/A"}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
